import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { DenseTable, type Column } from "../../components/index.ts";
import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { useIntentsPaged, useAgentDnaMetrics } from "./data.ts";
import { MetricTiles, ThreatPill, ReviewPill } from "./components.tsx";
import { DetailDrawer } from "./DetailDrawer.tsx";
import { IntentDetail } from "./IntentDetail.tsx";
import type { Intent } from "./types.ts";
import "./agentdna.css";

/**
 * Intents (AgentDNA port): the high-level goals being executed across the agent network. Clicking a
 * row opens a right-side drawer with the intent's full detail (overview + reliability), mirroring the
 * AgentDNA dashboard. A deep link from elsewhere (the Interactions drawer links an intent as
 * `/intents?intent=<id>`) auto-opens that intent's drawer and highlights its row. Viewer-level.
 */
export function IntentsScreen(): ReactNode {
  const { role } = useIdentity();
  const { data, loading } = useIntentsPaged(1);
  const { data: metrics } = useAgentDnaMetrics();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedIntentId = searchParams.get("intent");
  const [selected, setSelected] = useState<Intent | null>(null);

  // When arriving via `?intent=<id>` (e.g. the Interactions drawer's intent link), open that intent's
  // drawer once the list has loaded, then drop the param so a manual close stays closed.
  useEffect(() => {
    if (linkedIntentId === null) return;
    const match = data.items.find((x) => x.id === linkedIntentId);
    if (match === undefined) return;
    setSelected(match);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("intent");
        return next;
      },
      { replace: true },
    );
  }, [linkedIntentId, data.items, setSearchParams]);

  const state = resolveState(role, loading, data.items.length);
  const titleId = "intent-detail-title";

  const columns: readonly Column<Intent>[] = [
    { key: "name", header: "Intent", isRowHeader: true, cell: (r) => r.name },
    { key: "initiator", header: "Initiator", cell: (r) => r.initiator.name },
    { key: "interactions", header: "Interactions", align: "right", cell: (r) => r.interactionsCount },
    { key: "threats", header: "Threats", cell: (r) => <ThreatPill threat={r.threats > 0} /> },
    { key: "review", header: "Review", cell: (r) => <ReviewPill status={r.reviewStatus} /> },
    {
      key: "started",
      header: "Started",
      align: "right",
      cell: (r) => <time dateTime={isoAttr(r.started)}>{formatDateTime(r.started)}</time>,
    },
  ];

  return (
    <ScreenScaffold
      title="Intents"
      subtitle="High-level goals being executed across the agent network."
      state={state}
    >
      <MetricTiles
        tiles={[
          { label: "Total intents", value: metrics.intentCount },
          { label: "Agents engaged", value: metrics.agentCount },
          { label: "Apps engaged", value: metrics.appCount },
          { label: "Threats flagged", value: metrics.threatCount },
        ]}
      />
      <DenseTable
        caption="Intents"
        columns={columns}
        rows={data.items}
        rowKey={(r) => r.id}
        highlightRowKey={selected?.id ?? linkedIntentId}
        onRowClick={(r) => navigate(`/intents/${encodeURIComponent(r.id)}`)}
        rowLabel={(r) => `Open intent ${r.name}`}
        emptyMessage="No intents have been executed yet."
      />
      <DetailDrawer open={selected !== null} onClose={() => setSelected(null)} labelledBy={titleId}>
        {selected !== null && (
          <IntentDetail intent={selected} onClose={() => setSelected(null)} titleId={titleId} />
        )}
      </DetailDrawer>
    </ScreenScaffold>
  );
}

function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  loading: boolean,
  count: number,
): ScreenState {
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet. The server enforces access regardless."
          : "Intents need at least the viewer role.",
    };
  }
  if (loading) return { kind: "loading" };
  if (count === 0) return { kind: "empty", cause: "No intents have been executed for this organization yet." };
  return { kind: "ready" };
}
